import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, ChoiceCard, ChoiceGroup, Combobox, FormSection, Select, Stack, t, Textarea, TextField } from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { QualificationDraft, QualificationFields, QUESTIONS } from '../catalogue/QualificationFields';
import { useSession } from '../../lib/session';
import { api, unwrap } from '../../lib/api';
import { asName, isEnvironmentName, useEnvironmentName } from '../../lib/environments';

type Options = maintenance.paths['/servers/{server}/applications/options']['get']['responses']['200']['content']['application/json'];

export const NEW = '__new__';

/**
 * SPEC-034 § 6.1 step 1: client, project and environment, each one chosen among the existing ones or created here;
 * then (D103) the repository, whose deploy directories each make an application of the project. Kept in the address
 * (the strong authentication asked at the end brings the person back).
 */
export interface ProjectDraft {
  client: string | null;
  newClient: { title: string; contactEmail: string };
  project: string | null;
  newProject: { title: string; team: string | null };
  qualification: QualificationDraft;
  /** § 12.10: the team of a new project, one email per line. */
  members: { leads: string; developers: string };
  /** D103: the repository installed whole (owner/name). */
  repository: string;
  /** D103: each deploy directory as the person changed it (left out, its name, its role); unset: as proposed. */
  apps: Record<string, { off?: boolean; name?: string; role?: string | null; /** D105: its non-secret values. */ values?: Record<string, string> }>;
  /** D103: the domain of each exposed application, by its deploy directory. */
  domains: Record<string, string>;
  environment: string | null;
  newEnvironment: { name: string; type: string | null };
}

export const EMPTY_DRAFT: ProjectDraft = {
  client: null, newClient: { title: '', contactEmail: '' },
  project: null, newProject: { title: '', team: null },
  qualification: { criticality: null, healthData: null, dora: null, sensitivePersonalData: null, internetExposed: null },
  members: { leads: '', developers: '' },
  repository: '', apps: {}, domains: {},
  environment: null, newEnvironment: { name: '', type: null },
};

export function parseDraft(raw: string | null): ProjectDraft {
  if (!raw) return EMPTY_DRAFT;
  try {
    const d = JSON.parse(raw) as Partial<ProjectDraft>;
    return { ...EMPTY_DRAFT, ...d, apps: { ...(d.apps ?? {}) }, domains: { ...(d.domains ?? {}) }, qualification: { ...EMPTY_DRAFT.qualification, ...(d.qualification ?? {}) }, members: { ...EMPTY_DRAFT.members, ...(d.members ?? {}) } };
  } catch {
    return EMPTY_DRAFT;
  }
}

/** A name the catalogue accepts, from a title (« Questionnaire Loxam » → questionnaire-loxam). */
export const slugOf = (title: string) => title.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

/**
 * SPEC-034 § 10 (D98): outside production, the address suggested is a technical domain of the platform,
 * <client>-<project>[-<application>]-<environment>.<zone>, its record created by the platform. A part already
 * carried by the previous one is not repeated (client loxam, project loxam-questionnaire → loxam-questionnaire).
 */
export function technicalDomain(parts: { client: string | null; project: string | null; application?: string | null; environment: string | null }, zone: string | null): string {
  if (!zone || !parts.client || !parts.project || !parts.environment) return '';
  const words: string[] = [];
  for (const part of [parts.client, parts.project, parts.application, parts.environment]) {
    if (!part) continue;
    const previous = words.join('-');
    const last = words[words.length - 1] ?? '';
    words.push(previous && part.startsWith(`${previous}-`) ? part.slice(previous.length + 1)
      : last && part.startsWith(`${last}-`) ? part.slice(last.length + 1)
        : part === last || part === previous ? '' : part);
  }
  const label = words.filter(Boolean).join('-').replace(/-+/g, '-').slice(0, 63).replace(/-+$/, '');
  return label ? `${label}.${zone}` : '';
}

export const emails = (text: string) => text.split(/[\s,;]+/).map(e => e.trim().toLowerCase()).filter(Boolean);

/** The project's environments (each once, with its type), from its deployments. */
export const environmentsOf = (deployments: { environment: string; environmentType: string }[]) =>
  [...new Map(deployments.map(x => [x.environment, x.environmentType] as const)).entries()].map(([environment, environmentType]) => ({ environment, environmentType }));

/** What the choices are, existing or new: the summary, the branch by default, what the API receives (D103). */
export function resolveDraft(d: ProjectDraft, options: Options | undefined) {
  const client = d.client === NEW ? null : options?.clients.find(c => c.name === d.client) ?? null;
  const project = d.project === NEW ? null : options?.projects.find(p => p.name === d.project) ?? null;
  const environments = environmentsOf(project?.deployments ?? []);
  const environmentName = d.environment === NEW ? d.newEnvironment.name : d.environment;
  const environmentType = d.environment === NEW ? d.newEnvironment.type : environments.find(e => e.environment === d.environment)?.environmentType ?? null;
  // Step 1 « Projet » (client and project), step 2 « Dépôt » (the repository and the environment), D96, D103.
  const projectComplete = Boolean(
    (d.client === NEW ? d.newClient.title.trim() && d.newClient.contactEmail.trim() : client)
    && (d.project === NEW ? d.newProject.title.trim() && d.newProject.team && d.qualification.criticality && QUESTIONS.every(q => d.qualification[q] !== null) && emails(d.members.leads).length > 0 : project),
  );
  const repositoryComplete = Boolean(
    d.repository.trim()
    && (d.environment === NEW ? isEnvironmentName(d.newEnvironment.name) && d.newEnvironment.type : environments.some(e => e.environment === d.environment)),
  );
  const draft = {
    client: d.client === NEW ? { new: d.newClient } : { name: d.client ?? '' },
    project: d.project === NEW
      ? { new: { title: d.newProject.title, team: d.newProject.team ?? '', criticality: d.qualification.criticality ?? '', qualification: { healthData: d.qualification.healthData, dora: d.qualification.dora, sensitivePersonalData: d.qualification.sensitivePersonalData, internetExposed: d.qualification.internetExposed } } }
      : { name: d.project ?? '' },
    environment: { name: environmentName ?? '', ...(environmentType ? { type: environmentType } : {}) },
    ...(d.project === NEW ? { members: { leads: emails(d.members.leads), developers: emails(d.members.developers) } } : {}),
  };
  return {
    complete: projectComplete && repositoryComplete,
    projectComplete,
    repositoryComplete,
    draft,
    repository: d.repository.trim() || null,
    clientTitle: d.client === NEW ? d.newClient.title : client?.title ?? null,
    projectTitle: d.project === NEW ? d.newProject.title : project?.title ?? null,
    environmentName, environmentType,
    clientName: d.client === NEW ? slugOf(d.newClient.title) : d.client,
    projectName: d.project === NEW ? slugOf(d.newProject.title) : d.project,
    creates: [d.client === NEW, d.project === NEW, d.environment === NEW] as const,
  };
}

/** The field each error of the draft points to (the ErrorSummary's links), by the field's name in the API. */
export function fieldIdOf(field: string, d: ProjectDraft): string {
  if (field.startsWith('client.new.')) return `client-new-${field.slice('client.new.'.length)}`;
  if (field.startsWith('client')) return 'install-client';
  if (field === 'project.new.title' || field === 'project.new.team') return `project-new-${field.slice('project.new.'.length)}`;
  if (field.startsWith('project.new.')) return 'project-qualification';
  if (field.startsWith('project')) return 'install-project';
  if (field.startsWith('members')) return field === 'members.developers' ? 'members-developers' : 'members-leads';
  if (field.startsWith('repository')) return 'install-repository';
  // D103: a field of one application of the repository, in its row of the table.
  const app = /^applications\[(\d+)\]\.(name|role)$/.exec(field);
  if (app) return `app-${app[1]}-${app[2]}`;
  if (field.startsWith('applications')) return 'install-applications';
  if (field === 'environment.type') return 'environment-new-type';
  if (field.startsWith('environment')) return d.environment === NEW ? 'environment-new-name' : 'install-environment';
  return 'install-client';
}

type Part = 'project' | 'repository';
/** Which step of the assistant a field of the draft belongs to: the project's, the repository's (with the environment), or the applications'. */
export const partOf = (field: string): Part | 'applications' => (/^applications/.test(field) ? 'applications' : /^(repository|environment|deployment)/.test(field) ? 'repository' : 'project');

export function ProjectStep({ draft, onChange, options, errorOf, part }: {
  draft: ProjectDraft;
  onChange: (d: ProjectDraft) => void;
  options: Options;
  errorOf: (field: string) => string | undefined;
  /** D96, D103: step 1 « Projet » (client, project and, when new, its qualification and team), step 2 « Dépôt ». */
  part: Part;
}) {
  const { enums } = useSession();
  const envName = useEnvironmentName();
  const set = (patch: Partial<ProjectDraft>) => onChange({ ...draft, ...patch });
  const projects = options.projects.filter(p => p.client === draft.client);
  const project = projects.find(p => p.name === draft.project);
  const environments = environmentsOf(project?.deployments ?? []);
  // The repositories its applications already deploy from: one of them is likely the one to install.
  const known = [...new Set((project?.applications ?? []).map(a => a.repository).filter((r): r is string => Boolean(r)))];
  // The Combobox shows what is being created as its own value (DSM: « Créer « … » » from the list, D96).
  const withNew = (list: { value: string; label: string; description?: string }[], isNew: boolean, label: string) =>
    (isNew ? [...list, { value: NEW, label: t('install.project.created', { name: label || '…' }) }] : list);
  const candidates = options.teamCandidates;
  const people = (list: { name: string; email: string }[], chosen: string[]) =>
    [...list.map(c => ({ value: c.email, label: `${c.name} · ${c.email}` })), ...chosen.filter(e => !list.some(c => c.email === e)).map(e => ({ value: e, label: e }))];

  if (part === 'project') return (
    <Stack gap={24}>
      <FormSection layout="stacked" headingLevel={2} title={t('install.project.client')}>
        <Combobox
          id="install-client"
          label={t('install.project.pickClient')}
          searchable
          placeholder={t('newProject.client.searchPlaceholder')}
          options={withNew(options.clients.map(c => ({ value: c.name, label: c.title })), draft.client === NEW, draft.newClient.title)}
          value={draft.client}
          // The DSM Combobox gives a created entry to onChange too (its label as value): a name not in the list is new.
          onChange={(v: string | null) => (v && v !== NEW && !options.clients.some(c => c.name === v)
            ? set({ client: NEW, newClient: { ...draft.newClient, title: v.trim() }, project: NEW, environment: NEW })
            : set({ client: v, project: null, environment: null }))}
          creatable={options.canCreateClient}
          onCreate={() => undefined}
          hint={options.canCreateClient ? t('install.project.createHint') : undefined}
          error={errorOf('client.name') ?? errorOf('client')}
        />
        {draft.client === NEW && (
          <>
            <TextField id="client-new-title" label={t('newProject.client.name')} value={draft.newClient.title} required
              onChange={(e: { target: { value: string } }) => set({ newClient: { ...draft.newClient, title: e.target.value } })} error={errorOf('client.new.title')} />
            <TextField id="client-new-contactEmail" type="email" label={t('newProject.client.contact')} value={draft.newClient.contactEmail} required hint={t('newProject.client.contactHint')}
              onChange={(e: { target: { value: string } }) => set({ newClient: { ...draft.newClient, contactEmail: e.target.value } })} error={errorOf('client.new.contactEmail')} />
          </>
        )}
      </FormSection>

      {draft.client && (
        <FormSection layout="stacked" headingLevel={2} title={t('install.project.project')}>
          {draft.client !== NEW && (
            <Combobox
              id="install-project"
              label={t('install.project.pickProject')}
              searchable
              placeholder={t('install.project.searchProject')}
              options={withNew(projects.map(p => ({ value: p.name, label: p.title, description: t('declare.project.applications', { n: p.applications.length }) })), draft.project === NEW, draft.newProject.title)}
              value={draft.project}
              onChange={(v: string | null) => (v && v !== NEW && !projects.some(p => p.name === v)
                ? set({ project: NEW, newProject: { ...draft.newProject, title: v.trim() }, environment: NEW })
                : set({ project: v, environment: null }))}
              creatable={options.canCreateProject}
              onCreate={() => undefined}
              hint={options.canCreateProject ? t('install.project.createHint') : undefined}
              emptyText={t('install.project.noProject')}
              error={errorOf('project.name') ?? errorOf('project')}
            />
          )}
          {draft.project === NEW && (
            <>
              <TextField id="project-new-title" label={t('newProject.project.name')} value={draft.newProject.title} required
                onChange={(e: { target: { value: string } }) => set({ newProject: { ...draft.newProject, title: e.target.value } })} error={errorOf('project.new.title')} />
              <Select id="project-new-team" label={t('newProject.project.team')} searchable required value={draft.newProject.team}
                onChange={(v: string | null) => set({ newProject: { ...draft.newProject, team: v } })}
                options={options.teams.map(x => ({ value: x, label: x }))} placeholder={t('newProject.project.teamPlaceholder')} error={errorOf('project.new.team')} />
              {/* Mandatory at the creation (RG-006-1): no default answer. */}
              <div id="project-qualification">
                <QualificationFields value={draft.qualification} onChange={q => set({ qualification: q })} errors={Boolean(errorOf('project.new.criticality'))} />
              </div>
              {/* § 6.1 step 1, § 12.10: who deploys and operates its environments — picked in the Entra groups, as in « Équipe ». */}
              {candidates ? (
                <>
                  <Combobox id="members-leads" label={t('install.project.leads')} multiple options={people(candidates.leads, emails(draft.members.leads))}
                    value={emails(draft.members.leads)} onChange={(v: string[]) => set({ members: { ...draft.members, leads: v.join('\n') } })}
                    hint={t('install.project.leadsHint')} error={errorOf('members.leads') ?? errorOf('members')} />
                  <Combobox id="members-developers" label={t('install.project.developers')} multiple
                    options={people([...candidates.developers, ...candidates.leads], emails(draft.members.developers))}
                    value={emails(draft.members.developers)} onChange={(v: string[]) => set({ members: { ...draft.members, developers: v.join('\n') } })}
                    hint={t('install.project.developersHint')} error={errorOf('members.developers')} />
                </>
              ) : (
                <>
                  <Textarea id="members-leads" label={t('install.project.leads')} value={draft.members.leads} rows={2} hint={t('install.project.leadsHintText')}
                    onChange={(v: string) => set({ members: { ...draft.members, leads: v } })} error={errorOf('members.leads') ?? errorOf('members')} />
                  <Textarea id="members-developers" label={t('install.project.developers')} value={draft.members.developers} rows={3} optional hint={t('install.project.developersHintText')}
                    onChange={(v: string) => set({ members: { ...draft.members, developers: v } })} error={errorOf('members.developers')} />
                </>
              )}
            </>
          )}
          {draft.project && draft.project !== NEW && <Alert tone="info">{t('install.project.teamExisting')}</Alert>}
        </FormSection>
      )}
    </Stack>
  );

  return (
    <Stack gap={24}>
      <FormSection layout="stacked" headingLevel={2} title={t('install.project.repositorySection')}>
        {/* D103: the repository is installed whole; each of its deploy directories is an application (next step). */}
        <RepositoryPicker value={draft.repository} organisation={options.organisation} known={known}
          onChange={repository => set({ repository, apps: {}, domains: {} })} error={errorOf('repository')} />
      </FormSection>

      <FormSection layout="stacked" headingLevel={2} title={t('install.project.environment')}>
        {draft.project !== NEW && (
          <div id="install-environment"><ChoiceGroup label={t('install.project.pickEnvironment')}>
            {environments.map(x => (
              <ChoiceCard key={x.environment} variant="radio" title={envName(x.environment)}
                description={enums.environmentTypes.find(e => e.value === x.environmentType)?.label ?? x.environmentType}
                selected={draft.environment === x.environment} onClick={() => set({ environment: x.environment })} />
            ))}
            <ChoiceCard variant="radio" title={t('install.project.newEnvironment')} description={t('declare.environments.newBody')}
              selected={draft.environment === NEW} onClick={() => set({ environment: NEW })} />
          </ChoiceGroup></div>
        )}
        {draft.environment === NEW && (
          <>
            <TextField id="environment-new-name" label={t('declare.environments.name')} required mono value={draft.newEnvironment.name}
              placeholder={t('newProject.environments.namePlaceholder')} hint={t('declare.environments.nameHint')}
              onChange={(e: { target: { value: string } }) => set({ newEnvironment: { ...draft.newEnvironment, name: asName(e.target.value, 30) } })}
              error={errorOf('environment.name')} />
            <Select id="environment-new-type" label={t('declare.environments.type')} required value={draft.newEnvironment.type} options={enums.environmentTypes}
              placeholder={t('declare.environments.typeTodo')} onChange={(v: string | null) => set({ newEnvironment: { ...draft.newEnvironment, type: v } })}
              error={errorOf('environment.type')} />
          </>
        )}
      </FormSection>
    </Stack>
  );
}

/**
 * The repository of a new application, searched in the organisation's repositories (the list of « Nouveau projet »,
 * searched on the server: there can be hundreds), like the client, the project and the application above.
 */
function RepositoryPicker({ value, organisation, known, onChange, error }: { value: string; organisation: string; known: string[]; onChange: (repository: string) => void; error?: string }) {
  const [query, setQuery] = useState('');
  const repositories = useQuery({
    queryKey: ['github-repositories', query],
    queryFn: async () => unwrap(await api.github.GET('/repositories', { params: { query: { q: query || undefined } } })),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const items = (repositories.data?.items ?? []).filter(r => !r.archived);
  const options = items.map(r => ({
    value: r.fullName, label: r.fullName,
    description: [r.language, r.project ? t('install.project.repositoryHeldBy', { project: r.project.title }) : null].filter(Boolean).join(' · ') || undefined,
  }));
  // The project's own repositories first, while nothing is searched.
  if (!query) for (const r of [...known].reverse()) if (!options.some(o => o.value === r)) options.unshift({ value: r, label: r, description: t('install.project.repositoryOfProject') });
  // The chosen one keeps its label while the list shows another search.
  if (value && !options.some(o => o.value === value)) options.unshift({ value, label: value, description: undefined });
  return (
    <Combobox
      id="install-repository"
      label={t('install.project.repository')}
      searchable
      required
      placeholder={t('install.project.searchRepository', { organisation })}
      hint={t('install.project.repositoryHint')}
      options={options}
      value={value || null}
      onChange={(v: string | null) => onChange(v ?? '')}
      onQueryChange={setQuery}
      filter={false}
      loading={repositories.isFetching}
      emptyText={repositories.error ? t('install.project.repositoriesUnavailable') : t('install.project.noRepositoryFound')}
      error={error}
    />
  );
}
